import { useCallback, useRef } from 'preact/hooks';

import { Coords } from '../../UNSAFE_Floating';

import { getClientHints } from '../../utils/PRIVATE_clientHints';

type ContextMenuEventDetail = (
  | { gesture: 'mouse' | 'touch'; anchor: Coords }
  | { gesture: 'keyboard'; anchor: Element }
) & { currentTarget: Element; target: Element };

type ContextMenuHandler = (detail: ContextMenuEventDetail) => void;

type ContextMenuOptions = {
  isDisabled?: boolean;
};

/**
 * `useContextMenuGesture` hook to handle contextMenuGesture. Pure onContextMenu event is not enough to handle this since:
   1.- onContextMenu event is not supported on mobile safari.
   2.- onContextMenu can't be triggered using keyboard natively on Mac

   What event is running depends on the gesture:
   -> Mouse
      We use native onContextMenu event to support it. Nothing fancy about it, prevent default event to suppress the default browser context menu.
   -> Keyboard
      We rely on onKeyDown event, since mac doesn't run onContextMenu when pressing Shift-F10. We preventDefault
      if Shift-F10 is pressed so onContextMenu native event is not run in platforms that support it.
   -> Touching 
      This is platform specific.
      On machines that supports onContextMenu event and also touching (android, hybrid laptops) the native context menu 
      is used.
      On platforms where onContextMenu doesn't exist(IOS), the steps are the next:
      1.- We check if only one finger was used and also if the platform is IOS.
      2.- If there is a touch move we clear the timeout.   
      3.- If user doesn't move its finger and user holds enough we trigger contextMenu handler.
      4.- We clearTimeout on touchEnd.
 * 
 */
export const useContextMenuGesture = (
  onContextMenuHandler: ContextMenuHandler,
  contextMenuOptions: ContextMenuOptions = {
    isDisabled: false
  }
): { triggerProps: Record<string, any> } => {
  /*Use this ref to avoid triggering contextMenuHandler twice. This could happen due to Shift-F10 combination of keys.
   On firefox preventDefault inside keyDown event does not prevent contextMenu native event to be triggered. That's why 
   we use this ref instead. */
  const ignoreNativeContextMenuEvent = useRef(false);

  // TODO: We need to confirm if this type is correct or has to be changed. JET-59244
  const timerId = useRef<ReturnType<typeof setTimeout>>();

  const onContextMenu = useCallback(
    (event: PointerEvent) => {
      //Prevent default so we don't open browser context menu
      event.preventDefault();

      if (ignoreNativeContextMenuEvent.current) {
        ignoreNativeContextMenuEvent.current = false;
      } else {
        onContextMenuHandler({
          /*Pointer type can be touch/pen/mouse if its touch we passed touch if any other value we consider it a mouse 
            gesture */
          gesture: event.pointerType === 'touch' ? 'touch' : 'mouse',
          anchor: {
            x: event.clientX,
            y: event.clientY,
            contextElement: event.currentTarget as Element
          },
          currentTarget: event.currentTarget as Element,
          target: event.target as Element
        });
      }
    },
    [onContextMenuHandler]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'F10' && event.shiftKey) {
        ignoreNativeContextMenuEvent.current = true;

        const currentTarget = event.currentTarget as Element;
        onContextMenuHandler({
          gesture: 'keyboard',
          anchor: currentTarget,
          currentTarget,
          target: event.target as Element
        });
      }
    },
    [onContextMenuHandler]
  );

  const onTouchStart = useCallback(
    (event: TouchEvent) => {
      //We check if the touch was using a single finger and if the platform is ios since there contextMenu event does not exist
      if (event.touches.length === 1 && getClientHints().platform === 'ios') {
        const touchobj = event.touches[0];
        //According to mozilla docs: "The value of event.currentTarget is only available while the event is being handled"
        //Due to this we need to save this element on a const
        const currentTarget = event.currentTarget as Element;

        timerId.current = setTimeout(() => {
          onContextMenuHandler({
            gesture: 'touch',
            anchor: { x: touchobj.clientX, y: touchobj.clientY, contextElement: currentTarget },
            currentTarget: currentTarget,
            target: event.target as Element
          });
        }, PRESS_HOLD_TIME);
      }
    },
    [onContextMenuHandler]
  );

  const onTouchMove = useCallback(() => {
    clearTimeout(timerId.current);
  }, []);

  const onTouchEnd = useCallback(() => {
    clearTimeout(timerId.current);
  }, []);

  const triggerProps = contextMenuOptions.isDisabled
    ? {}
    : { onContextMenu, onKeyDown, onTouchStart, onTouchEnd, onTouchMove };

  return { triggerProps };
};

//Time needed to hold touch to be considered context menu triggering
const PRESS_HOLD_TIME = 750;
